/*
 * REPORTING AUDIT (F5) — OWNER ONLY. What the Founder conversational tool PREPARED, as metadata:
 * entry point, outcome, topic identifiers, metric and source identifiers, period. Never a
 * question, an answer, a figure or a member.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ScrollText } from "lucide-react";
import { Panel, Note, Chip, fmt, FreshnessNote } from "./ConsolePrimitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/founder/conversation-audit`;

const TopicChips = ({ rows, tone, prefix }) => (
  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
    {(rows || []).map((r) => (
      <Chip key={String(r.key)} tone={tone} testId={`${prefix}-${r.key}`}>{r.key}: {r.count}</Chip>
    ))}
  </div>
);

export const AuditHistoryView = ({ params }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(API, { params });
      setData(res.data);
    } catch (e) {
      setError("The reporting audit could not be read just now.");
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <header className="fc__section-head">
        <h2 data-testid="fc-audit-title">Reporting Audit</h2>
        <p>Answers prepared: <b data-testid="fc-audit-prepared">{fmt(data?.answers_prepared?.value)}</b></p>
      </header>
      <Panel title="What was prepared" icon={ScrollText} testId="fc-audit-panel" sub={data?.delivery_note}>
        <TopicChips rows={data?.topics?.topics_served} tone="good" prefix="fc-audit-served" />
        <TopicChips rows={data?.topics?.topics_refused} tone="warn" prefix="fc-audit-refused" />
        <TopicChips rows={data?.topics?.topics_unavailable} tone="quiet" prefix="fc-audit-unavailable" />
        <table className="fc-table" style={{ marginTop: 14 }}>
          <thead><tr><th>When</th><th>Who</th><th>Entry</th><th>Outcome</th><th>Served</th><th>Refused</th></tr></thead>
          <tbody>
            {(data?.recent || []).map((r, i) => (
              <tr key={`${r.at}-${i}`} data-testid={`fc-audit-row-${i}`}>
                <td>{(r.at || "").slice(0, 16).replace("T", " ")}</td>
                <td>{r.actor_email}</td>
                <td>{r.entry}</td>
                <td>{r.outcome}</td>
                <td>{(r.topics_served || []).join(", ")}</td>
                <td>{(r.topics_refused || []).join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !(data.recent || []).length && <Note testId="fc-audit-empty">No reporting answer was prepared in this period.</Note>}
        {data && <Note>Most recent {data.limit} records in the period.</Note>}
        {error && <Note testId="fc-audit-error">{error}</Note>}
      </Panel>
      {data?.freshness && <FreshnessNote freshness={data.freshness} />}
    </>
  );
};
